/**
 * ЛОГИКА ЭКРАНА «ВМЕСТЕ» · окно 4 запуска — статистика участников.
 *
 * Считает всё ядро (`core/leaderboard.py`): дни занятий по часам КАЖДОГО человека,
 * число ответов, согласие на показ имени, «занимаются N из M». Витрина не считает
 * ни одной цифры — она их раскладывает и говорит по-русски.
 *
 * ⭐ Главная цифра — ДНИ, вторая — ответы: дни показывают привычку, а ответы главной
 *   цифрой награждали бы того, кто много щёлкает за один заход. Порядок строк тоже
 *   пришёл из ядра — здесь мы его не пересортировываем.
 * ⛔ «Мест» в спортивном смысле нет: ни медалей, ни подиума, ни номеров. Цель экрана —
 *   «рядом занимаются», а не «ты 47-й» (решение рамки запуска 31.07).
 * ⛔ Имени без согласия здесь взяться неоткуда: API присылает уже готовое «участник».
 *
 * Ни DOM, ни сети — правила проверяются запуском:
 *   cd Lang/web && node tools/check-leaderboard.mjs
 */

export type PeriodKey = "week" | "month" | "all";

/** Строка таблицы — ровно то, что отдаёт ядро (набор полей там закрыт списком). */
export interface LbRow {
  name: string;
  named: boolean;
  is_me: boolean;
  days: number;
  answers: number;
  last_day: string | null;
}

export interface LbTable {
  period: string;
  title: string;
  since: string | null;
  tz: string;
  studying: number;
  total_members: number | null;
  rows: LbRow[];
}

/** Ответ `GET /leaderboard`. `asked` — спрашивали ли уже про показ имени. */
export interface LbOut {
  periods: Partial<Record<PeriodKey, LbTable>>;
  order: string[];
  total_members: number | null;
  opt_in: boolean;
  asked: boolean;
}

export const TABS: { key: PeriodKey; label: string }[] = [
  { key: "week", label: "Неделя" },
  { key: "month", label: "Месяц" },
  { key: "all", label: "Всего" },
];

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = Math.abs(n) % 10;
  const m100 = Math.abs(n) % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export const daysLabel = (n: number) => `${n} ${plural(n, "день", "дня", "дней")}`;
export const answersLabel = (n: number) => `${n} ${plural(n, "ответ", "ответа", "ответов")}`;

/** «2026-08-18» → «18.08». Пусто → прочерк, а не «Invalid Date». */
export function dateShort(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ""));
  return m ? `${m[3]}.${m[2]}` : "—";
}

/** Сдвинуть дату «YYYY-MM-DD» на N дней — без часовых поясов, чисто календарно. */
function shift(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Когда человек занимался. «Сегодня» и «вчера» считаем от дня, который прислало ядро
 * (у него день по часам человека), а НЕ от часов устройства: телефон в дороге может
 * стоять в чужом поясе, и «сегодня» на экране разошлось бы с «сегодня» в занятии.
 */
export function whenLabel(last: string | null, today: string | null): string {
  if (!last) return "—";
  if (today) {
    if (last === today) return "сегодня";
    if (last === shift(today, -1)) return "вчера";
  }
  return dateShort(last);
}

/** Итоговая строка. M нет (ворота выключены или Telegram не ответил) → молчим про «из». */
export function studyingLabel(t: Pick<LbTable, "studying" | "total_members">): string {
  const n = t.studying;
  const word = plural(n, "Занимается", "Занимаются", "Занимаются");
  return t.total_members == null
    ? `${word}: ${n} ${plural(n, "человек", "человека", "человек")}`
    : `${word}: ${n} из ${t.total_members}`;
}

/** Подпись окна периода. У «всего» границы нет — и подписи тоже. */
export function sinceLabel(t: Pick<LbTable, "since">): string {
  return t.since ? `с ${dateShort(t.since)}, по твоим часам` : "за всё время";
}

export function emptyLabel(period: string): string {
  return period === "all"
    ? "Пока никто не занимался. Таблица наполнится сама — как только люди начнут."
    : "За этот период ещё никто не занимался.";
}

/** Своя строка в таблице (её ядро помечает `is_me`). */
export function meRow(t: LbTable | undefined): LbRow | null {
  return (t?.rows || []).find((r) => r.is_me) ?? null;
}

/** Человек уже в таблице — то есть хоть раз занимался (а не просто открыл экран). */
export function inTable(out: LbOut | null): boolean {
  if (!out) return false;
  return Object.values(out.periods).some((t) => (meRow(t)?.days ?? 0) > 0);
}

/**
 * ⭐ Спрашиваем про показ имени РОВНО ОДИН РАЗ и только у того, кто уже попал в
 * таблицу: спрашивать «показывать ли имя» у того, кого в ней нет, — беспокоить зря.
 * Записанный отказ (`asked`) — это ответ, а не «ещё не спросили»; второй раз не лезем.
 */
export function shouldAsk(out: LbOut | null): boolean {
  return !!out && !out.asked && inTable(out);
}

/** Подпись состояния согласия — и в спросе, и в настройках говорим одно и то же. */
export function consentSummary(optIn: boolean): string {
  return optIn
    ? "Другие видят твоё имя в общей таблице."
    : "В общей таблице ты — «участник», без имени. Цифры считаются всё равно.";
}

/** Что именно мы показываем — говорим прямо, до нажатия. */
export const CONSENT_DETAIL =
  "Покажем только имя. Ни ссылки на профиль, ни адреса в Telegram не показываем никому.";

/** Почему в таблице людей меньше, чем подписчиков, — честное объяснение, а не оправдание. */
export function membersHint(total: number | null): string {
  return total == null
    ? "В таблице те, кто уже занимался."
    : `В таблице те, кто уже занимался. Остальные из ${total} просто ещё не начали.`;
}
